// P3-012: Enemy behavior system (GDD section 4.5.6)

import type { EnemyState, OverloadCheckResult } from '../types/battle-internal';
import type { TilePosition } from '../types/board';
import { HP_THRESHOLD_EVENTS, OVERLOAD_PROBABILITY, ENEMY_TYPE_CONFIGS } from '../data/enemy-configs';
import { chance, shuffle } from '@/shared/utils/random';
import { useBattleStore } from '../stores/battleStore';
import { useBoardStore } from '../stores/boardStore';
import { BOARD_COLS, BOARD_ROWS } from '../data/board-constants';

/**
 * Check HP threshold events (66% and 33%).
 * Returns buffs/debuffs to apply if thresholds are crossed.
 */
export function checkHPThresholds(enemy: EnemyState): {
  triggered: boolean;
  event?: (typeof HP_THRESHOLD_EVENTS)[number];
  tilesToLock?: TilePosition[];
} {
  const hpPercent = enemy.hp / enemy.maxHp;

  // Check 66% threshold first (lighter event)
  if (!enemy.triggered66 && hpPercent <= 0.66) {
    const event = HP_THRESHOLD_EVENTS.find((e) => e.threshold === 0.66);
    if (event) {
      return { triggered: true, event };
    }
  }

  // Check 33% threshold (heavier event)
  if (!enemy.triggered33 && hpPercent <= 0.33) {
    const event = HP_THRESHOLD_EVENTS.find((e) => e.threshold === 0.33);
    if (event) {
      // Generate random tile positions to lock
      const tilesToLock = getRandomUnlockedPositions(event.lockTiles);
      return { triggered: true, event, tilesToLock };
    }
  }

  return { triggered: false };
}

/**
 * Apply HP threshold event effects to battle state.
 */
export function applyHPThresholdEvent(
  event: (typeof HP_THRESHOLD_EVENTS)[number],
  tilesToLock?: TilePosition[],
): void {
  const battleStore = useBattleStore.getState();
  const boardStore = useBoardStore.getState();

  // Set enemy phase and demand-change duration
  battleStore.setEnemyPhase(event.phase);
  battleStore.setDemandChangeTurns(event.duration);

  // Mark threshold as triggered
  if (event.threshold === 0.66) {
    battleStore.setEnemyThresholdTriggered('66');
  } else if (event.threshold === 0.33) {
    battleStore.setEnemyThresholdTriggered('33');
  }

  // Lock tiles if needed (heavy demand change)
  if (tilesToLock && tilesToLock.length > 0) {
    boardStore.lockTiles(tilesToLock);
  }
}

/**
 * Apply enemy type-specific mechanics each turn.
 */
export function applyEnemyTypeMechanic(
  enemyType: EnemyState['type'],
  currentTurn: number,
): { deadlineReduced: boolean; legacyActive: boolean } {
  const battleStore = useBattleStore.getState();
  let deadlineReduced = false;
  let legacyActive = false;

  const config = ENEMY_TYPE_CONFIGS[enemyType];
  if (!config?.mechanic) {
    return { deadlineReduced, legacyActive };
  }

  switch (config.mechanic.type) {
    case 'DEADLINE_REDUCE': {
      // IMPATIENT: every N turns, deadline-1
      const interval = config.mechanic.interval ?? 4;
      if (currentTurn > 0 && currentTurn % interval === 0) {
        battleStore.reduceDeadline(1);
        deadlineReduced = true;
      }
      break;
    }

    case 'TILE_CD_INCREASE': {
      // LEGACY: initial N turns, tiles have slower movement (simulated as extra swap restrictions)
      // In the MVP, this is tracked but the actual mechanic is handled by the UI layer
      const duration = config.mechanic.duration ?? 3;
      if (currentTurn <= duration) {
        legacyActive = true;
      }
      break;
    }
  }

  return { deadlineReduced, legacyActive };
}

/**
 * Check for overload event this turn.
 * Probability based on overload level (GDD section 4.2.3):
 *   - none: 0%
 *   - light: 5%
 *   - medium: 15%
 *   - heavy: 30%
 *
 * Effect: 1-2 random agents have their skills disabled for this turn.
 */
export function checkOverload(
  overloadLevel: 'none' | 'light' | 'medium' | 'heavy',
): OverloadCheckResult {
  const probability = OVERLOAD_PROBABILITY[overloadLevel] ?? 0;

  if (probability === 0 || !chance(probability)) {
    return { triggered: false, affectedAgentIds: [] };
  }

  // Pick 1-2 random agents to disable
  const battleStore = useBattleStore.getState();
  const team = battleStore.team;
  const numAffected = Math.random() < 0.5 ? 1 : 2;

  const shuffledAgents = shuffle([...team]);
  const affected = shuffledAgents.slice(0, numAffected);
  const affectedIds = affected.map((a) => a.agentId);

  // Disable skills for affected agents
  for (const agentId of affectedIds) {
    battleStore.disableAgentSkill(agentId);
  }

  // Track stats
  battleStore.incrementStat('overloadEvents');

  return {
    triggered: true,
    affectedAgentIds: affectedIds,
  };
}

/**
 * Get random unlocked tile positions from the board.
 */
function getRandomUnlockedPositions(count: number): TilePosition[] {
  const boardStore = useBoardStore.getState();
  const { tiles } = boardStore;

  const candidates: TilePosition[] = [];
  for (let x = 0; x < BOARD_COLS; x++) {
    for (let y = 0; y < BOARD_ROWS; y++) {
      const tile = tiles[x]?.[y];
      if (tile && !tile.locked) {
        candidates.push({ x, y });
      }
    }
  }

  // Shuffle and pick
  shuffle(candidates);
  return candidates.slice(0, count);
}

/**
 * Handle demand change phase expiry based on buff ticking.
 * Call after buffs have been ticked to check if phase should return to normal.
 */
export function checkDemandChangeExpiry(): void {
  const battleStore = useBattleStore.getState();
  const enemy = battleStore.enemy;
  if (!enemy) return;
  if (enemy.phase === 'normal') return;

  // Decrement the demand-change remaining turns
  battleStore.decrementDemandChangeTurns();

  // Check if demand-change has expired
  const updatedEnemy = useBattleStore.getState().enemy;
  if (updatedEnemy && updatedEnemy.demandChangeTurnsRemaining <= 0) {
    battleStore.setEnemyPhase('normal');
  }
}
